import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import { getNavigationById } from './api';
import { mapNavigationResolveToDomain } from './mappers';
import type { NavigationMatch } from './types';

export const NAVIGATION_RESOLVE_KEY = 'navigation-resolve';

export interface UseResolveNavigationIdResult {
    data: NavigationMatch[];
    isLoading: boolean;
    isFetching: boolean;
    isFetched: boolean;
    error: unknown;
}

export function useResolveNavigationId(
    id: number | null,
    options?: { enabled?: boolean }
): UseResolveNavigationIdResult {
    const enabled = (options?.enabled ?? true) && id != null && id > 0;

    const query = useQuery({
        queryKey: [NAVIGATION_RESOLVE_KEY, id],
        queryFn: () => {
            if (id == null) {
                throw new Error('navigation id required');
            }
            return getNavigationById(id);
        },
        enabled,
        staleTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
    });

    const data = useMemo(() => {
        if (!enabled) {
            return [];
        }
        return mapNavigationResolveToDomain(query.data?.data);
    }, [enabled, query.data?.data]);

    return {
        data,
        isLoading: query.isPending,
        isFetching: query.isFetching,
        isFetched: query.isFetched,
        error: query.error,
    };
}
